// Тач-фолбэк для drag-and-drop: HTML5 DnD на многих мобильных браузерах не работает
// (в частности, iOS Safari игнорирует draggable), поэтому для pointerType 'touch'/'pen'
// используем ручной long-press + pointermove/elementFromPoint. Мышь обслуживает нативный
// HTML5 DnD (см. VariantColumn/VariantsBoard/TimelineCard) — эта же карточка просто не
// реагирует на onPointerDown, если pointerType === 'mouse'.
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { DragPayload } from './DndContext';
import { useDndInternal } from './DndContext';
import { computeInsertIndex, locateVariantDropZone } from './geometry';
import { LONG_PRESS_MS, MOVE_CANCEL_PX } from './constants';

export interface TouchDropTarget {
  variantId: string;
  index: number;
}

export interface UseTouchDragOptions {
  /** что тащим — вычисляется вызывающим компонентом (карточка знает свой segmentId/variantId/index) */
  payload: DragPayload;
  /** запрет активации (например, идёт инлайн-редактирование имени варианта) */
  disabled?: boolean;
  /** вызывается на pointerup после long-press; null = «наружу» (см. resolveDrop) */
  onDrop(target: TouchDropTarget | null, payload: DragPayload): void;
}

export interface TouchDragHandlers {
  onPointerDown(e: ReactPointerEvent): void;
  onPointerMove(e: ReactPointerEvent): void;
  onPointerUp(e: ReactPointerEvent): void;
  onPointerCancel(e: ReactPointerEvent): void;
}

export interface UseTouchDragResult {
  handlers: TouchDragHandlers;
  /** true, пока карточка «поднята» после long-press — применить lift-стиль (масштаб/тень) */
  lifted: boolean;
}

export function useTouchDrag(opts: UseTouchDragOptions): UseTouchDragResult {
  const dnd = useDndInternal();
  const [lifted, setLifted] = useState(false);

  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const liftedRef = useRef(false);
  const elRef = useRef<HTMLElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  // держим свежую копию колбэков/payload, чтобы не пересобирать таймер на каждый рендер
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseCapture = useCallback(() => {
    const el = elRef.current;
    const id = pointerIdRef.current;
    if (el && id !== null && el.hasPointerCapture?.(id)) {
      try {
        el.releasePointerCapture(id);
      } catch {
        // элемент уже отпущен браузером — не критично
      }
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    releaseCapture();
    startRef.current = null;
    liftedRef.current = false;
    pointerIdRef.current = null;
    elRef.current = null;
    setLifted(false);
    dnd.setTouchActive(false);
    dnd.end();
  }, [clearTimer, releaseCapture, dnd]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (optsRef.current.disabled) return;
      if (e.pointerType === 'mouse') return; // мышь — через нативный HTML5 DnD
      startRef.current = { x: e.clientX, y: e.clientY };
      pointerIdRef.current = e.pointerId;
      elRef.current = e.currentTarget as HTMLElement;
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        liftedRef.current = true;
        setLifted(true);
        dnd.setTouchActive(true);
        dnd.begin(optsRef.current.payload);
        try {
          elRef.current?.setPointerCapture(e.pointerId);
        } catch {
          // не критично — просто продолжаем без захвата указателя
        }
      }, LONG_PRESS_MS);
    },
    [clearTimer, dnd],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!startRef.current) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;

      if (!liftedRef.current) {
        // Движение до срабатывания long-press — это скролл, а не намерение тащить.
        if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
          clearTimer();
          startRef.current = null;
        }
        return;
      }

      // Активный драг — не даём странице скроллиться под пальцем.
      e.preventDefault();
      const zone = locateVariantDropZone(e.clientX, e.clientY);
      if (zone) {
        const index = computeInsertIndex(zone.container, e.clientY);
        dnd.setOver({ variantId: zone.variantId, index });
      } else {
        dnd.setOver(null);
      }
    },
    [clearTimer, dnd],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      const wasLifted = liftedRef.current;
      clearTimer();
      if (!wasLifted) {
        startRef.current = null;
        return;
      }
      const zone = locateVariantDropZone(e.clientX, e.clientY);
      const target: TouchDropTarget | null = zone
        ? { variantId: zone.variantId, index: computeInsertIndex(zone.container, e.clientY) }
        : null;
      optsRef.current.onDrop(target, optsRef.current.payload);
      reset();
    },
    [clearTimer, reset],
  );

  const onPointerCancel = useCallback(() => {
    reset();
  }, [reset]);

  // Подчистить таймер при демонтаже карточки посреди long-press.
  useEffect(() => clearTimer, [clearTimer]);

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    lifted,
  };
}
